import React, { useState, useMemo } from 'react';
import { IMPORT_DAYS_AHEAD } from './googleConfig';
import { toDateStr } from './timeUtils';

const WEEKDAYS = ['日', '月', '火', '水', '木', '金', '土'];

// 選べる最初の年
const FIRST_YEAR = 2026;

const isSameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

function TaskCalendar({ selectedDate, onSelectDate, oldestTaskDate }) {
  const [cursor, setCursor] = useState(
    new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1)
  );

  const today = new Date();

  /**
   * 選べる最後の日。
   * 明日から IMPORT_DAYS_AHEAD 日ぶんなので、今日から数えると同じ日数だけ後。
   * 例）今日が 8/30、60日ぶんなら 8/31 〜 10/29 が対象で、最後は 10/29。
   * 日数は googleConfig.js で決める。
   */
  const lastDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + IMPORT_DAYS_AHEAD);
    d.setHours(23, 59, 59, 999);
    return d;
  }, []);

  const lastYear = lastDate.getFullYear();
  const lastMonth = lastDate.getMonth();

  const years = useMemo(() => {
    const out = [];
    for (let y = FIRST_YEAR; y <= lastYear; y++) out.push(y);
    return out;
  }, [lastYear]);

  const year = cursor.getFullYear();
  const month = cursor.getMonth();

  /** その年で選べる月（最後の年だけ制限がかかる） */
  const months = useMemo(() => {
    const max = year === lastYear ? lastMonth : 11;
    const out = [];
    for (let m = 0; m <= max; m++) out.push(m);
    return out;
  }, [year, lastYear, lastMonth]);

  const setYear = (y) => {
    // 選んだ年で今の月が範囲外になる場合は、選べる最後の月に寄せる
    const maxMonth = y === lastYear ? lastMonth : 11;
    setCursor(new Date(y, Math.min(month, maxMonth), 1));
  };

  const setMonth = (m) => setCursor(new Date(year, m, 1));

  const shiftMonth = (delta) => {
    const next = new Date(year, month + delta, 1);
    if (next.getFullYear() < FIRST_YEAR) return;
    if (next > new Date(lastYear, lastMonth, 1)) return;
    setCursor(next);
  };

  const canGoPrev = !(year === FIRST_YEAR && month === 0);
  const canGoNext = !(year === lastYear && month === lastMonth);

  // ===== 日付の並び =====
  const firstDayOfWeek = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells = [];
  for (let i = 0; i < firstDayOfWeek; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);

  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));

  return (
    <div className="calendar-container">
      <div className="calendar-header">
        <button onClick={() => shiftMonth(-1)} disabled={!canGoPrev}>
          ←
        </button>

        <div className="calendar-selects">
          <select value={year} onChange={(e) => setYear(Number(e.target.value))}>
            {years.map((y) => (
              <option key={y} value={y}>
                {y}年
              </option>
            ))}
          </select>
          <select value={month} onChange={(e) => setMonth(Number(e.target.value))}>
            {months.map((m) => (
              <option key={m} value={m}>
                {m + 1}月
              </option>
            ))}
          </select>
        </div>

        <button onClick={() => shiftMonth(1)} disabled={!canGoNext}>
          →
        </button>
      </div>

      <div className="calendar-weekdays">
        {WEEKDAYS.map((w) => (
          <div key={w} className="weekday">
            {w}
          </div>
        ))}
      </div>

      {weeks.map((week, wi) => (
        <div key={wi} className="calendar-week">
          {week.map((day, di) => {
            if (!day) return <div key={di} className="calendar-day empty" />;

            const dayStr = toDateStr(day);
            
            // 有効範囲判定: oldestDate <= day <= maxFutureDate
            const isInRange = oldestTaskDate 
              ? (dayStr >= oldestTaskDate && day <= lastDate)
              : (day <= lastDate);
            
            const classes = [
              'calendar-day',
              isSameDay(day, selectedDate) ? 'selected' : '',
              isSameDay(day, today) ? 'today' : '',
              !isInRange ? 'disabled' : '',
            ]
              .filter(Boolean)
              .join(' ');

            return (
              <div
                key={di}
                className={classes}
                onClick={() => isInRange && onSelectDate(day)}
              >
                {day.getDate()}
              </div>
            );
          })}
        </div>
      ))}

      <div className="calendar-note">
        予定の取り込みは明日から{IMPORT_DAYS_AHEAD}日ぶんです
      </div>
    </div>
  );
}

export default TaskCalendar;
